/* eslint-disable react/prop-types */
/*
 * Copyright (c) 2026. Tooling Team
 */

import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import Layout from "../components/layout/Layout";
import { RouteDisplay } from "../components/navigation/RouteDisplay";
import { PATHS } from "../utils/constants";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { Button } from "@uhg-abyss/web/ui/Button";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import useMedDomainEISSummary from "../hooks/useMedDomainEISSummary";

export function MedDomainEISDetails() {
  const { correlation_id } = useParams();
  const location = useLocation();
  const formattedEndTime = location.state?.end_time
    ? moment(location.state.end_time).format("YYYY.MM.DD HH:mm")
    : "";

  const {
    state: { medDomainBuildSummaryEisData },
    isLoadingSummary,
    isErrorSummary,
    errorSummary,
    refetchSummary,
  } = useMedDomainEISSummary(correlation_id);
  const errorMessage =
    errorSummary?.data?.message ||
    errorSummary?.error ||
    "Unable to load modern summary data.";

  const columns = useMemo(() => {
    if (
      !Array.isArray(medDomainBuildSummaryEisData) ||
      !medDomainBuildSummaryEisData.length
    ) {
      return [];
    }

    return Object.keys(medDomainBuildSummaryEisData[0]).map((key) => ({
      header: key
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" "),
      accessorKey: key,
    }));
  }, [medDomainBuildSummaryEisData]);

  const dataTableProps = useDataTable({
    initialData: medDomainBuildSummaryEisData,
    initialColumns: columns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
    },
  });

  useEffect(() => {
    if (
      Array.isArray(medDomainBuildSummaryEisData) &&
      typeof dataTableProps.setData === "function"
    ) {
      dataTableProps.setData(medDomainBuildSummaryEisData, true);
    }
  }, [medDomainBuildSummaryEisData, dataTableProps]);

  useEffect(() => {
    if (columns.length && typeof dataTableProps.setColumns === "function") {
      dataTableProps.setColumns(columns);
    }
  }, [columns, dataTableProps]);

  return (
    <Layout>
      <RouteDisplay pageRouteDisplay={[PATHS.medDomainEISDetails]} />
      <div className="grid">
        <div className="row mb-3">
          <h4>Correlation ID: {correlation_id}</h4>
          {formattedEndTime && <p>Build End Time: {formattedEndTime}</p>}
        </div>

        {isLoadingSummary && !medDomainBuildSummaryEisData.length && (
          <div className="row mb-3">Loading modern summary data...</div>
        )}

        {isErrorSummary && (
          <div className="row mb-3">
            <p>{errorMessage}</p>
            <Button onClick={refetchSummary}>Retry</Button>
          </div>
        )}

        {!isLoadingSummary &&
          !isErrorSummary &&
          Array.isArray(medDomainBuildSummaryEisData) &&
          medDomainBuildSummaryEisData.length === 0 && (
            <div className="row mb-3" style={{ paddingLeft: "12px" }}>
              No modern summary records were found for this build.
            </div>
          )}

        {!!medDomainBuildSummaryEisData.length && (
          <div className="row mb-4">
            <DataTable
              title="Med Domain - Build Summary"
              tableState={dataTableProps}
            >
              <DataTable.Table />
            </DataTable>
          </div>
        )}
      </div>
    </Layout>
  );
}
